import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { commissionsService, itinerariesService, suppliersService } from '../../services';

// Voyager Pro reskin (Figma CommissionsPage's "Submit Commission Request" modal)
const inputCls = "w-full px-4 py-2.5 border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] bg-[var(--muted)]/20 focus:border-[var(--accent)] outline-none transition-colors";
const labelCls = "block text-xs font-medium text-[var(--muted-foreground)] mb-1.5";

const CommissionForm = ({ onClose, onSuccess, selectedItinerary = null, draftCommission = null }) => {
  const { user } = useAuth();
  const isEditingDraft = !!draftCommission;
  const isIndependentAgent = user?.role === 'independent_agent';
  const [loading, setLoading] = useState(false);
  const [itineraries, setItineraries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    itinerary_id: draftCommission?.itinerary_id || selectedItinerary?.id || '',
    first_name: draftCommission?.first_name || user?.firstName || user?.first_name || '',
    last_name: draftCommission?.last_name || user?.lastName || user?.last_name || '',
    email: draftCommission?.email || user?.email || '',
    phone: draftCommission?.phone || user?.phone || user?.phone_number || user?.phoneNumber || user?.mobile || user?.business_phone || '',
    reservation_number: draftCommission?.reservation_number || '',
    guest_first_name: draftCommission?.guest_first_name || '',
    guest_last_name: draftCommission?.guest_last_name || '',
    supplier: draftCommission?.supplier || '',
    booking_date: draftCommission?.booking_date || new Date().toISOString().split('T')[0],
    travel_date: draftCommission?.travel_date || '',
    gross_commission: draftCommission?.gross_commission || '',
    notes: draftCommission?.notes || ''
  });

  // Debug logging for phone number population
  useEffect(() => {
    if (user) {
      console.log('📞 CommissionForm - User object:', user);
      console.log('📞 CommissionForm - User phone:', user.phone);
      console.log('📞 CommissionForm - Form phone:', formData.phone);

      // Update phone if user has phone data but form phone is empty or whitespace
      const userPhone = user.phone || user.phone_number || user.phoneNumber || user.mobile || user.business_phone;
      if (userPhone && (!formData.phone || formData.phone.trim() === '')) {
        console.log('📞 CommissionForm - Updating form phone from user object:', userPhone);
        setFormData(prev => ({ ...prev, phone: userPhone }));
      }
    }
  }, [user, formData.phone]);

  useEffect(() => {
    fetchItineraries();
    fetchSuppliers();
  }, []);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  const fetchItineraries = async () => {
    try {
      // Fetch completed/archived itineraries (is_archived = 1)
      const data = await itinerariesService.getCompleted();
      // Filter for travel that has actually ended
      const completedItineraries = data.filter(itinerary =>
        new Date(itinerary.travel_end_date) < new Date()
      );
      setItineraries(completedItineraries);
    } catch (error) {
      console.error('Error fetching itineraries:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const data = await suppliersService.getAll();
      setSuppliers(data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.phone || formData.phone.trim() === '') {
      newErrors.phone = 'Phone number is required';
    }
    if (!formData.reservation_number) {
      newErrors.reservation_number = 'Reservation number is required';
    }
    if (!formData.guest_first_name) {
      newErrors.guest_first_name = 'Guest first name is required';
    }
    if (!formData.guest_last_name) {
      newErrors.guest_last_name = 'Guest last name is required';
    }
    if (!formData.supplier) {
      newErrors.supplier = 'Supplier is required';
    }
    if (!formData.travel_date) {
      newErrors.travel_date = 'Travel date is required';
    }
    if (!formData.gross_commission || parseFloat(formData.gross_commission) <= 0) {
      newErrors.gross_commission = 'Valid gross commission amount is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      itinerary_id: '',
      first_name: user?.firstName || user?.first_name || '',
      last_name: user?.lastName || user?.last_name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      reservation_number: '',
      guest_first_name: '',
      guest_last_name: '',
      supplier: '',
      booking_date: new Date().toISOString().split('T')[0],
      travel_date: '',
      gross_commission: '',
      notes: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      let response;

      if (isEditingDraft) {
        // Submitting a draft (convert draft to pending). validateForm() above
        // only checks in-memory formData - submitDraft() takes no body and
        // re-validates against whatever is currently saved in the DB, so any
        // edits made in this form (e.g. filling in a previously-missing
        // travel_date) must be persisted first, or the server re-validates
        // stale data and rejects with a confusing 400 even though the form
        // looks complete on screen.
        const draftData = {
          ...formData,
          gross_commission: formData.gross_commission ? parseFloat(formData.gross_commission) : null
        };
        console.log('💾 CommissionForm - Saving draft edits before submit:', draftCommission.id, draftData);
        await commissionsService.updateDraft(draftCommission.id, draftData);

        console.log('📝 CommissionForm - Submitting draft ID:', draftCommission.id);
        response = await commissionsService.submitDraft(draftCommission.id);
      } else {
        // Creating new commission as pending
        const submissionData = {
          ...formData,
          gross_commission: parseFloat(formData.gross_commission),
          status: 'pending' // Explicitly set status to pending for submission
        };

        console.log('📞 CommissionForm - Submitting data:', submissionData);
        console.log('📞 CommissionForm - Phone in submission:', submissionData.phone);

        response = await commissionsService.submit(submissionData);
      }

      if (onSuccess) {
        onSuccess(response);
      }

      resetForm();
      console.log('📞 CommissionForm - Form reset, phone set to:', user?.phone);

      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error submitting commission:', error);

      // Handle duplicate reservation number error with helpful message
      if (error.response?.data?.error === 'Duplicate reservation number found') {
        setErrors({
          submit: `This reservation number already exists in the system.\n\nPlease verify the reservation number is correct, or check if this commission has already been submitted.`
        });
      } else {
        setErrors({ submit: error.response?.data?.error || 'Failed to submit commission. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAsDraft = async (e) => {
    e.preventDefault();

    // For drafts, we don't require all fields - only basic validation
    const draftErrors = {};
    if (!formData.first_name?.trim()) draftErrors.first_name = 'First name is required';
    if (!formData.last_name?.trim()) draftErrors.last_name = 'Last name is required';
    if (!formData.email?.trim()) draftErrors.email = 'Email is required';

    if (Object.keys(draftErrors).length > 0) {
      setErrors(draftErrors);
      return;
    }

    setLoading(true);
    try {
      let response;
      const draftData = {
        ...formData,
        gross_commission: formData.gross_commission ? parseFloat(formData.gross_commission) : null
      };

      if (isEditingDraft) {
        // Updating existing draft
        console.log('💾 CommissionForm - Updating draft ID:', draftCommission.id, draftData);
        response = await commissionsService.updateDraft(draftCommission.id, draftData);
      } else {
        // Creating new draft
        draftData.status = 'draft'; // Set status to draft for new drafts
        console.log('💾 CommissionForm - Creating new draft:', draftData);
        response = await commissionsService.submit(draftData);
      }

      if (onSuccess) {
        onSuccess(response);
      }

      resetForm();

      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      setErrors({ submit: error.response?.data?.error || 'Failed to save draft' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle itinerary selection with auto-fill
  const handleItineraryChange = async (itineraryId) => {
    console.log('🔍 CommissionForm - Itinerary selected:', itineraryId);

    // Update itinerary_id first
    setFormData(prev => ({ ...prev, itinerary_id: itineraryId }));

    if (itineraryId) {
      try {
        console.log('📡 CommissionForm - Fetching itinerary details...');
        // Fetch full itinerary details
        const itinerary = await itinerariesService.getById(itineraryId);
        console.log('✅ CommissionForm - Itinerary data received:', itinerary);

        // Parse lead_traveler JSON if available
        let leadTraveler = null;
        if (itinerary.lead_traveler) {
          try {
            leadTraveler = typeof itinerary.lead_traveler === 'string'
              ? JSON.parse(itinerary.lead_traveler)
              : itinerary.lead_traveler;
          } catch (e) {
            console.warn('Failed to parse lead_traveler:', e);
          }
        }

        // Parse client_info JSON if available
        let clientInfo = null;
        if (itinerary.client_info) {
          try {
            clientInfo = typeof itinerary.client_info === 'string'
              ? JSON.parse(itinerary.client_info)
              : itinerary.client_info;
          } catch (e) {
            console.warn('Failed to parse client_info:', e);
          }
        }

        // Extract guest names with priority: lead_traveler > client_info > client_name
        const firstName = leadTraveler?.firstName || clientInfo?.firstName || itinerary.client_name?.split(' ')[0] || '';
        const lastName = leadTraveler?.lastName || clientInfo?.lastName || itinerary.client_name?.split(' ').slice(1).join(' ') || '';

        // Format travel_date from ISO to yyyy-MM-dd
        let travelDate = '';
        if (itinerary.travel_end_date) {
          const dateStr = String(itinerary.travel_end_date);
          if (dateStr.includes('T')) {
            travelDate = dateStr.split('T')[0];
          } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            travelDate = dateStr;
          } else {
            const dateObj = new Date(dateStr);
            if (!isNaN(dateObj.getTime())) {
              const year = dateObj.getUTCFullYear();
              const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
              const day = String(dateObj.getUTCDate()).padStart(2, '0');
              travelDate = `${year}-${month}-${day}`;
            }
          }
        }

        // Auto-populate guest information and travel date
        const autoFillData = {
          itinerary_id: itineraryId,
          guest_first_name: firstName.trim(),
          guest_last_name: lastName.trim(),
          travel_date: travelDate
        };

        console.log('📝 CommissionForm - Auto-fill data:', autoFillData);

        setFormData(prev => ({
          ...prev,
          ...autoFillData
        }));

        // Clear any errors for auto-filled fields
        setErrors(prev => ({
          ...prev,
          guest_first_name: '',
          guest_last_name: '',
          travel_date: ''
        }));

        console.log('✨ CommissionForm - Auto-fill complete');
      } catch (error) {
        console.error('❌ CommissionForm - Error fetching itinerary details:', error);
        // If fetch fails, just keep the itinerary_id selected
      }
    }
  };

  // Calculate agent commission preview with proper rounding (rounds .005 up)
  const calculateCommission = (grossCommission, rate) => {
    const value = (parseFloat(grossCommission) * rate) / 100;
    return (Math.round((value + Number.EPSILON) * 100) / 100).toFixed(2);
  };

  const commissionRate = user?.commissionPercentage || user?.commission_percentage || (isIndependentAgent ? 100 : 70);
  const agentCommission = formData.gross_commission ?
    calculateCommission(formData.gross_commission, commissionRate) :
    '0.00';

  const isDisneySupplier = (formData.supplier || '').trim().toLowerCase() === 'disney';

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)] sticky top-0 bg-white rounded-t-2xl z-10">
          <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)]">
            {isEditingDraft
              ? 'Edit Draft Commission'
              : (isIndependentAgent ? 'Record Commission' : 'Submit Commission Request')
            }
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              type="button"
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[var(--muted)] text-[var(--muted-foreground)] transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-6">
          {errors.submit && (
            <div className="px-4 py-3 bg-red-50 border-2 border-red-400 rounded-xl text-sm text-red-800 whitespace-pre-line leading-relaxed">
              <strong>⚠️ Submission Error</strong>
              <div className="mt-2">{errors.submit}</div>
            </div>
          )}

          {/* Agent Information */}
          <section>
            <h3 className="text-sm font-bold text-[var(--foreground)] mb-4">Agent Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>First Name *</label>
                <input type="text" value={formData.first_name} readOnly className={`${inputCls} bg-[var(--muted)] cursor-not-allowed`} />
              </div>
              <div>
                <label className={labelCls}>Last Name *</label>
                <input type="text" value={formData.last_name} readOnly className={`${inputCls} bg-[var(--muted)] cursor-not-allowed`} />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input type="email" value={formData.email} readOnly className={`${inputCls} bg-[var(--muted)] cursor-not-allowed`} />
              </div>
              <div>
                <label className={labelCls}>Phone Number *</label>
                <input type="tel" value={formData.phone} readOnly className={`${inputCls} bg-[var(--muted)] cursor-not-allowed`} />
              </div>
            </div>
          </section>

          <div className="border-t border-[var(--border)]" />

          {/* Booking Information */}
          <section>
            <h3 className="text-sm font-bold text-[var(--foreground)] mb-4">Booking Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Itinerary (Optional)</label>
                <select value={formData.itinerary_id} onChange={(e) => handleItineraryChange(e.target.value)} className={`${inputCls} appearance-none cursor-pointer`}>
                  <option value="">Select an itinerary</option>
                  {itineraries.map(itinerary => (
                    <option key={itinerary.id} value={itinerary.id}>
                      {itinerary.title} - {itinerary.client_name || 'Unknown Client'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Supplier *</label>
                <select
                  value={formData.supplier}
                  onChange={(e) => handleInputChange('supplier', e.target.value)}
                  className={`${inputCls} appearance-none cursor-pointer`}
                  style={{ borderColor: errors.supplier ? '#EF4444' : undefined }}
                >
                  <option value="">Select a supplier</option>
                  {suppliers.map(supplier => (
                    <option key={supplier.id} value={supplier.name}>{supplier.name}</option>
                  ))}
                </select>
                {errors.supplier && <span className="text-xs text-red-800">{errors.supplier}</span>}
                <div className="mt-1 text-xs">
                  <span className="text-[var(--muted-foreground)]">Supplier not listed? </span>
                  <Link to="/agent/supplier-request" className="text-[var(--accent)] hover:underline">
                    Please submit your request here
                  </Link>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className={labelCls}>Reservation / Booking Number *</label>
                <input
                  type="text"
                  value={formData.reservation_number}
                  onChange={(e) => handleInputChange('reservation_number', e.target.value)}
                  placeholder="Enter reservation number"
                  className={inputCls}
                  style={{ borderColor: errors.reservation_number ? '#EF4444' : undefined }}
                />
                {errors.reservation_number && <span className="text-xs text-red-800">{errors.reservation_number}</span>}
                {isDisneySupplier && (
                  <div className="mt-2 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-900 leading-relaxed">
                    <strong>Walt Disney World hotel or package booking?</strong> Please use the <strong>Travel Plan number</strong>, not the booking confirmation number. The Travel Plan number can be found on the agent copy of the booking confirmation email you received from Disney.
                  </div>
                )}
              </div>
              <div>
                <label className={labelCls}>Booking Date *</label>
                <input
                  type="date"
                  value={formData.booking_date}
                  onChange={(e) => handleInputChange('booking_date', e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Travel Date *</label>
                <input
                  type="date"
                  value={formData.travel_date}
                  onChange={(e) => handleInputChange('travel_date', e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className={inputCls}
                  style={{ borderColor: errors.travel_date ? '#EF4444' : undefined }}
                />
                {errors.travel_date && <span className="text-xs text-red-800">{errors.travel_date}</span>}
              </div>
            </div>
          </section>

          <div className="border-t border-[var(--border)]" />

          {/* Guest Information */}
          <section>
            <h3 className="text-sm font-bold text-[var(--foreground)] mb-4">Guest Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Guest First Name *</label>
                <input
                  type="text"
                  value={formData.guest_first_name}
                  onChange={(e) => handleInputChange('guest_first_name', e.target.value)}
                  placeholder="Enter guest first name"
                  className={inputCls}
                  style={{ borderColor: errors.guest_first_name ? '#EF4444' : undefined }}
                />
                {errors.guest_first_name && <span className="text-xs text-red-800">{errors.guest_first_name}</span>}
              </div>
              <div>
                <label className={labelCls}>Guest Last Name *</label>
                <input
                  type="text"
                  value={formData.guest_last_name}
                  onChange={(e) => handleInputChange('guest_last_name', e.target.value)}
                  placeholder="Enter guest last name"
                  className={inputCls}
                  style={{ borderColor: errors.guest_last_name ? '#EF4444' : undefined }}
                />
                {errors.guest_last_name && <span className="text-xs text-red-800">{errors.guest_last_name}</span>}
              </div>
            </div>
          </section>

          <div className="border-t border-[var(--border)]" />

          {/* Commission Information */}
          <section>
            <h3 className="text-sm font-bold text-[var(--foreground)] mb-4">Commission Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Gross Commission ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.gross_commission}
                  onChange={(e) => handleInputChange('gross_commission', e.target.value)}
                  placeholder="0.00"
                  className={inputCls}
                  style={{ borderColor: errors.gross_commission ? '#EF4444' : undefined }}
                />
                {errors.gross_commission && <span className="text-xs text-red-800">{errors.gross_commission}</span>}
              </div>
              <div>
                <label className={labelCls}>Agent Commission ({commissionRate}%)</label>
                <div className="px-4 py-2.5 border border-emerald-200 rounded-xl text-sm font-bold text-emerald-700 bg-emerald-50">
                  ${agentCommission}
                </div>
                <span className="text-xs text-[var(--muted-foreground)] italic mt-1 inline-block">Automatically calculated</span>
              </div>
            </div>
          </section>

          <div className="border-t border-[var(--border)]" />

          {/* Additional Information */}
          <section>
            <h3 className="text-sm font-bold text-[var(--foreground)] mb-4">Additional Information</h3>
            <div>
              <label className={labelCls}>Notes (Optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Enter any additional notes or comments"
                rows="4"
                className={`${inputCls} resize-y`}
              />
            </div>
          </section>

          <div className="flex justify-end gap-3 pt-2 border-t border-[var(--border)]">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-5 py-2.5 border border-[var(--border)] rounded-xl text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={handleSaveAsDraft}
              disabled={loading}
              title="Save commission information without completing all fields"
              className="flex items-center gap-2 px-5 py-2.5 border border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--accent)] rounded-xl text-sm font-semibold hover:bg-[var(--accent)]/20 transition-colors disabled:opacity-50"
            >
              {loading ? '💾 Saving...' : '💾 Save as Draft'}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-5 py-2.5 bg-[var(--primary)] text-white rounded-xl text-sm font-semibold hover:bg-[var(--primary)]/90 transition-colors disabled:opacity-50"
            >
              {loading
                ? (isIndependentAgent ? 'Recording...' : 'Submitting...')
                : (isIndependentAgent ? 'Record Commission' : 'Submit Commission Request')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CommissionForm;
